import { useIncidentDetail } from 'core/components/JianDetail/hooks/useIncidentDetail';
import {
  // @ts-ignore - TODO: API再生成後に削除
  AdministeredMedication,
  BaseInfoOccupationEnum,
  BaseInfoSexEnum,
  DrCarDrHeliDispatchEnum,
  ECGWaveformEnum,
  ECGWaveRemarksEnum,
  EmsTeamMemberPositionEnum,
  IncidentTypeEnum,
  IntubationDevice,
  JCSEnum,
  MeasureTimingEnum,
  OperationCriteriaDetailEnum,
  PupilReactionEnum,
  RequestSummaryADLEnum,
  RequestSummaryMedicalHistoryEnum,
  TrafficAccidentAccidentTypeEnum,
  TrafficAccidentSituationEnum,
  TreatmentIvAccessTimingEnum,
  VitalBtPartEnum,
  VitalO2MeanEnum,
} from 'core/gen/schemas';

/**
 * 傷病者の性別の日本語ラベルマッピング
 */
const PATIENT_SEX_LABELS = {
  [BaseInfoSexEnum.MALE]: '男性',
  [BaseInfoSexEnum.FEMALE]: '女性',
  [BaseInfoSexEnum.UNKNOWN]: '不明',
} as const satisfies Record<keyof typeof BaseInfoSexEnum, string>;

/**
 * 傷病者の性別の日本語ラベルを取得する関数
 *
 * @param patientSex - 傷病者の性別
 * @returns 日本語ラベル
 */
export const getPatientSexLabel = (
  patientSex: keyof typeof BaseInfoSexEnum | null | undefined
): string => {
  if (!patientSex || !(patientSex in PATIENT_SEX_LABELS)) {
    return '不明';
  }

  return PATIENT_SEX_LABELS[patientSex];
};

/**
 * 事案種別の日本語ラベルマッピング
 */
const INCIDENT_TYPE_LABELS = {
  [IncidentTypeEnum.FIRE]: '火災',
  [IncidentTypeEnum.NATURAL_DISASTER]: '自然災害',
  [IncidentTypeEnum.DROWNING]: '水難事故',
  [IncidentTypeEnum.TRAFFIC]: '交通事故',
  [IncidentTypeEnum.WORK_ACCIDENT]: '労働災害',
  [IncidentTypeEnum.SPORTS]: '運動競技',
  [IncidentTypeEnum.GENERAL_INJURY]: '一般負傷',
  [IncidentTypeEnum.ASSAULT]: '加害',
  [IncidentTypeEnum.SELF_INJURY]: '自損行為',
  [IncidentTypeEnum.SUDDEN_ILLNESS]: '急病',
  [IncidentTypeEnum.HOSPITAL_TRANSFER]: '転院搬送',
  [IncidentTypeEnum.HOSPITAL_TRANSFER_SUPPORT]: '転院搬送支援', // 新潟では利用しない
  [IncidentTypeEnum.DOCTOR_TRANSPORT]: '医師搬送',
  [IncidentTypeEnum.EQUIPMENT_TRANSPORT]: '資機材輸送',
  [IncidentTypeEnum.OTHER]: 'その他',
} as const satisfies Record<keyof typeof IncidentTypeEnum, string>;

/**
 * 事案種別の日本語ラベルを取得する関数
 *
 * @param incidentType - 事案種別
 * @returns 日本語ラベル
 */
export const getIncidentTypeLabel = (
  incidentType: keyof typeof IncidentTypeEnum | null | undefined
): string => {
  if (!incidentType || !(incidentType in INCIDENT_TYPE_LABELS)) {
    return '不明';
  }

  return INCIDENT_TYPE_LABELS[incidentType];
};

/**
 * 救急隊員の役職の日本語ラベルマッピング
 */
const EMS_TEAM_MEMBER_POSITION_LABELS = {
  [EmsTeamMemberPositionEnum.LEADER]: '隊長',
  [EmsTeamMemberPositionEnum.SUB_LEADER]: '副隊長',
  [EmsTeamMemberPositionEnum.MEMBER]: '隊員',
} as const satisfies Record<keyof typeof EmsTeamMemberPositionEnum, string>;

/**
 * 救急隊員の役職の日本語ラベルを取得する関数
 *
 * @param emsTeamMemberPosition - 救急隊員の役職
 * @returns 日本語ラベル
 */
export const getEmsTeamMemberPositionLabel = (
  emsTeamMemberPosition:
    | keyof typeof EmsTeamMemberPositionEnum
    | null
    | undefined
): string => {
  if (
    !emsTeamMemberPosition ||
    !(emsTeamMemberPosition in EMS_TEAM_MEMBER_POSITION_LABELS)
  ) {
    return '不明';
  }

  return EMS_TEAM_MEMBER_POSITION_LABELS[emsTeamMemberPosition];
};

/**
 * 職業の日本語ラベルマッピング
 */
const OCCUPATION_LABELS = {
  [BaseInfoOccupationEnum.UNEMPLOYED]: '無職',
  [BaseInfoOccupationEnum.EMPLOYEE]: '会社員',
  [BaseInfoOccupationEnum.GOVERNMENT]: '公務員',
  [BaseInfoOccupationEnum.SELF]: '自営業',
  [BaseInfoOccupationEnum.STUDENT]: '学生',
  [BaseInfoOccupationEnum.CHILD]: '児童',
  [BaseInfoOccupationEnum.INFANT]: '乳幼児',
  [BaseInfoOccupationEnum.OTHER]: 'その他',
  [BaseInfoOccupationEnum.UNKNOWN]: '不明',
  // 以下は新潟では利用しない
  [BaseInfoOccupationEnum.NONPROFIT]: '団体職',
  [BaseInfoOccupationEnum.MEDICAL]: '医療関係',
  [BaseInfoOccupationEnum.SERVICE_WORKER]: 'サービス業',
  [BaseInfoOccupationEnum.ELEMENTARY_SCHOOL_STUDENT]: '小学生',
  [BaseInfoOccupationEnum.MIDDLE_SCHOOL_STUDENT]: '中学生',
  [BaseInfoOccupationEnum.HIGH_SCHOOL_STUDENT]: '高校生',
  [BaseInfoOccupationEnum.TECHNICAL_COLLEGE_STUDENT]: '高等専門学校生',
  [BaseInfoOccupationEnum.JUNIOR_COLLEGE_STUDENT]: '短大生',
  [BaseInfoOccupationEnum.UNIVERSITY_STUDENT]: '大学生',
  [BaseInfoOccupationEnum.VOCATIONAL_SCHOOL_STUDENT]: '各種学校生',
  [BaseInfoOccupationEnum.FREELANCER]: '自由業',
  [BaseInfoOccupationEnum.MAFF]: '農林漁業',
  [BaseInfoOccupationEnum.LABORER]: '労働者(日雇い等)',
  [BaseInfoOccupationEnum.PART_TIME_WORKER]: 'パート・アルバイト',
  [BaseInfoOccupationEnum.HOUSEWIFE]: '主婦',
  [BaseInfoOccupationEnum.HOMEMAKER]: '主夫・主婦',
} as const satisfies Record<keyof typeof BaseInfoOccupationEnum, string>;

/**
 * 職業の日本語ラベルを取得する関数
 *
 * @param occupation - 職業
 * @returns 日本語ラベル
 */
export const getOccupationLabel = (
  occupation: keyof typeof BaseInfoOccupationEnum | null | undefined
): string => {
  if (!occupation || !(occupation in OCCUPATION_LABELS)) {
    return '不明';
  }
  return OCCUPATION_LABELS[occupation];
};

/**
 * 傷病者の既往歴の日本語ラベルマッピング
 */
const REQUEST_SUMMARY_MEDICAL_HISTORY_LABELS = {
  [RequestSummaryMedicalHistoryEnum.HYPER_TENSION]: '高血圧',
  [RequestSummaryMedicalHistoryEnum.DIABETES]: '糖尿病',
  [RequestSummaryMedicalHistoryEnum.MYOCARDIAL_INFARCTION]: '心筋梗塞',
  [RequestSummaryMedicalHistoryEnum.ANGINA]: '狭心症',
  [RequestSummaryMedicalHistoryEnum.HEART_FAILURE]: '心不全',
  [RequestSummaryMedicalHistoryEnum.HYPER_LIPIDEMIA]: '高脂血症',
  [RequestSummaryMedicalHistoryEnum.ASTHMA]: '喘息',
  [RequestSummaryMedicalHistoryEnum.COPD]: 'COPD',
  [RequestSummaryMedicalHistoryEnum.CEREBRAL_INFARCTION]: '脳梗塞',
  [RequestSummaryMedicalHistoryEnum.CEREBRAL_HEMORRHAGE]: '脳出血',
  [RequestSummaryMedicalHistoryEnum.DEMENTIA]: '認知症',
  [RequestSummaryMedicalHistoryEnum.EPILEPSY]: 'てんかん',
  [RequestSummaryMedicalHistoryEnum.KIDNEY_FAILURE]: '腎不全',
  // 以下は新潟では利用しない
  [RequestSummaryMedicalHistoryEnum.CANCER]: '悪性腫瘍',
  [RequestSummaryMedicalHistoryEnum.DIALYSIS]: '透析',
  [RequestSummaryMedicalHistoryEnum.MENTAL_ILLNESS]: '精神病',
  [RequestSummaryMedicalHistoryEnum.ARRHYTHMIA]: '不整脈',
  [RequestSummaryMedicalHistoryEnum.OTHER_HEART_DISEASES]: '他の心疾患',
  [RequestSummaryMedicalHistoryEnum.CEREBROVASCULAR]: '脳血管',
  [RequestSummaryMedicalHistoryEnum.RESPIRATORY_DISEASES]: '呼吸器',
  [RequestSummaryMedicalHistoryEnum.LIVER_DISEASES]: '肝疾患',
  [RequestSummaryMedicalHistoryEnum.KIDNEY_DISEASES]: '腎疾患',
  [RequestSummaryMedicalHistoryEnum.DIGESTIVE_DISEASES]: '消化器',
  [RequestSummaryMedicalHistoryEnum.CIRCULATORY_SYSTEM]: '循環器疾患',
  [RequestSummaryMedicalHistoryEnum.NERVOUS_SYSTEM]: '神経系疾患',
  [RequestSummaryMedicalHistoryEnum.ENDOCRINE_SYSTEM]: '内分泌系疾患',
  [RequestSummaryMedicalHistoryEnum.HEMATOLOGY]: '血液疾患',
  [RequestSummaryMedicalHistoryEnum.OBSTETRICS_AND_GYNECOLOGY]: '婦人科疾患',
  [RequestSummaryMedicalHistoryEnum.INFECTIOUS_DISEASES]: '感染症',
  [RequestSummaryMedicalHistoryEnum.RENAL_UROLOGY]: '腎・泌尿器疾患',
  [RequestSummaryMedicalHistoryEnum.ALLERGY]: 'アレルギー',
  [RequestSummaryMedicalHistoryEnum.OTHER]: 'その他',
} as const satisfies Record<
  keyof typeof RequestSummaryMedicalHistoryEnum,
  string
>;

/**
 * 傷病者の既往歴の日本語ラベルを取得する関数
 *
 * @param medicalHistory - 傷病者の既往歴
 * @returns 日本語ラベル
 */
export const getRequestSummaryMedicalHistoryLabel = (
  medicalHistory:
    | keyof typeof RequestSummaryMedicalHistoryEnum
    | null
    | undefined
): string => {
  if (
    !medicalHistory ||
    !(medicalHistory in REQUEST_SUMMARY_MEDICAL_HISTORY_LABELS)
  ) {
    return '';
  }
  return REQUEST_SUMMARY_MEDICAL_HISTORY_LABELS[medicalHistory];
};

/**
 * 最終食事の日本語ラベルを取得する関数
 *
 * @param isLastMeal - 最終食事の有無
 * @returns 日本語ラベル
 */
export const getRequestSummaryIsLastMealLabel = (
  isLastMeal: boolean | null | undefined
): string => {
  if (!isLastMeal) {
    return '不明';
  }
  return '特定';
};

/**
 * 傷病者の日常生活能力の日本語ラベルマッピング
 */
const REQUEST_SUMMARY_ADL_LABELS = {
  [RequestSummaryADLEnum.INDEPENDENT]: '自立',
  [RequestSummaryADLEnum.BEDRIDDEN]: '寝たきり',
  [RequestSummaryADLEnum.UNKNOWN]: '不明',
  // 以下は新潟では利用しない
  [RequestSummaryADLEnum.CARE_REQUIRED]: '介護あり',
  [RequestSummaryADLEnum.PARTIAL_ASSISTANCE]: '一部介助',
  [RequestSummaryADLEnum.FULL_ASSISTANCE]: '全介助',
} as const satisfies Record<keyof typeof RequestSummaryADLEnum, string>;

/**
 * 傷病者の日常生活能力の日本語ラベルを取得する関数
 *
 * @param adlLevel - 傷病者の日常生活能力
 * @returns 日本語ラベル
 */
export const getRequestSummaryADLLabel = (
  adlLevel: keyof typeof RequestSummaryADLEnum | null | undefined
): string => {
  if (!adlLevel || !(adlLevel in REQUEST_SUMMARY_ADL_LABELS)) {
    return '';
  }
  return REQUEST_SUMMARY_ADL_LABELS[adlLevel];
};

/**
 * 実施基準.対象の日本語ラベルマッピング
 */
const OPERATION_CRITERIA_DETAIL_LABELS = {
  [OperationCriteriaDetailEnum.CRITICAL]: '重篤',
  [OperationCriteriaDetailEnum.MYOCARDIAL_INFARCTION]: '心筋梗塞',
  [OperationCriteriaDetailEnum.STROKE]: '脳卒中',
  [OperationCriteriaDetailEnum.SEVERE_TRAUMA]: '重症外傷',
  [OperationCriteriaDetailEnum.PREGNANT]: '妊産婦',
  [OperationCriteriaDetailEnum.PEDIATRIC]: '小児',
} as const satisfies Record<keyof typeof OperationCriteriaDetailEnum, string>;

/**
 * 実施基準.対象の日本語ラベルを取得する関数
 *
 * @param operationCriteriaDetail - 実施基準.対象
 * @returns 日本語ラベル
 */
export const getOperationCriteriaDetailLabel = (
  operationCriteriaDetail:
    | keyof typeof OperationCriteriaDetailEnum
    | null
    | undefined
): string => {
  if (
    !operationCriteriaDetail ||
    !(operationCriteriaDetail in OPERATION_CRITERIA_DETAIL_LABELS)
  ) {
    return '';
  }
  return OPERATION_CRITERIA_DETAIL_LABELS[operationCriteriaDetail];
};

/**
 * 測定タイミングの日本語ラベルマッピング
 */
const MEASURE_TIMING_LABELS = {
  [MeasureTimingEnum.CONTACT]: '接触時',
  [MeasureTimingEnum.IN_AMBULANCE]: '車内',
  [MeasureTimingEnum.TRANSPORTING]: '搬送中',
  // 以下は新潟では利用しない
  // TODO: 対応する日本語が不明あとで確認する
  [MeasureTimingEnum.SITE]: '現場',
  [MeasureTimingEnum.AMBULANCE_ACCOMMODATION]: '車内収容時',
  [MeasureTimingEnum.HOSPITAL_ARRIVAL]: '病着時',
  [MeasureTimingEnum.BEFORE_HOSPITAL_ARRIVAL]: '病着前',
  [MeasureTimingEnum.SITE_DEPARTURE]: '現発時',
  [MeasureTimingEnum.OTHER]: 'その他',
} as const satisfies Record<keyof typeof MeasureTimingEnum, string>;

/**
 * 測定タイミングの日本語ラベルを取得する関数
 *
 * @param measureTiming - 測定タイミング
 * @returns 日本語ラベル
 */
export const getMeasureTimingLabel = (
  measureTiming: keyof typeof MeasureTimingEnum | null | undefined
): string => {
  if (!measureTiming || !(measureTiming in MEASURE_TIMING_LABELS)) {
    return '';
  }
  return MEASURE_TIMING_LABELS[measureTiming];
};
/**
 * JCSの日本語ラベルマッピング
 */
const JCS_LABELS = {
  [JCSEnum.NUMBER_0]: '清明',
  [JCSEnum['1-1']]: 'JCS1-1',
  [JCSEnum['1-2']]: 'JCS1-2',
  [JCSEnum['1-3']]: 'JCS1-3',
  [JCSEnum['2-10']]: 'JCS2-10',
  [JCSEnum['2-20']]: 'JCS2-20',
  [JCSEnum['2-30']]: 'JCS2-30',
  [JCSEnum['3-100']]: 'JCS3-100',
  [JCSEnum['3-200']]: 'JCS3-200',
  [JCSEnum['3-300']]: 'JCS3-300',
  // 以下は新潟では利用しない
  [JCSEnum.OTHER]: 'その他',
} as const satisfies Record<JCSEnum, string>;

export const getJCSLabel = (jcs: JCSEnum | null | undefined): string => {
  if (!jcs || !(jcs in JCS_LABELS)) {
    return '';
  }
  return JCS_LABELS[jcs];
};

/**
 * 酸素投与方法(器具)の日本語ラベルマッピング
 */
const VITAL_O2_MEAN_LABELS = {
  [VitalO2MeanEnum.NASAL_CANNULA]: '鼻カニューレ',
  [VitalO2MeanEnum.FACE_MASK]: 'フェイスマスク',
  [VitalO2MeanEnum.VENTURI]: 'ベンチュリー',
  [VitalO2MeanEnum.RESERVOIR_MASK]: 'リザーバーマスク',
  [VitalO2MeanEnum.NONE]: 'なし',
  // 以下は新潟では利用しない
  [VitalO2MeanEnum.BVM]: 'BVM',
  [VitalO2MeanEnum.VENTILATOR]: 'ベンチレーター',
  [VitalO2MeanEnum.OTHER]: 'その他',
} as const satisfies Record<keyof typeof VitalO2MeanEnum, string>;

/**
 * 酸素投与方法(器具)の日本語ラベルを取得する関数
 *
 * @param o2Mean - 酸素投与方法(器具)
 * @returns 日本語ラベル
 */
export const getVitalO2MeanLabel = (
  o2Mean: keyof typeof VitalO2MeanEnum | null | undefined
): string => {
  if (!o2Mean || !(o2Mean in VITAL_O2_MEAN_LABELS)) {
    return '';
  }
  return VITAL_O2_MEAN_LABELS[o2Mean];
};

/**
 * 体温測定部位の日本語ラベルマッピング
 */
const VITAL_BT_PART_LABELS = {
  [VitalBtPartEnum.AXILLA]: '腋窩',
  [VitalBtPartEnum.TYMPANIC_MEMBRANE]: '鼓膜',
  // 以下は新潟では利用しない
  [VitalBtPartEnum.NON_CONTACT]: '非接触',
} as const satisfies Record<keyof typeof VitalBtPartEnum, string>;

/**
 * 体温測定部位の日本語ラベルを取得する関数
 *
 * @param btPart - 体温測定部位
 * @returns 日本語ラベル
 */
export const getVitalBtPartLabel = (
  btPart: keyof typeof VitalBtPartEnum | null | undefined
): string => {
  if (!btPart || !(btPart in VITAL_BT_PART_LABELS)) {
    return '';
  }
  return VITAL_BT_PART_LABELS[btPart];
};

/**
 * 心電図波形の日本語ラベルマッピング
 */
const ECG_WAVEFORM_LABELS = {
  [ECGWaveformEnum.NORMAL]: '正常',
  [ECGWaveformEnum.SINUS_RHYTHM]: '洞調律',
  [ECGWaveformEnum.SINUS_BRADYCARDIA]: '洞徐脈',
  [ECGWaveformEnum.SINUS_TACHYCARDIA]: '洞頻脈',
  [ECGWaveformEnum.ASYSTOLE]: '心静止',
  [ECGWaveformEnum.PEA]: 'PEA',
  [ECGWaveformEnum.VENTRICULAR_FIBRILLATION]: '心室細動(VF)',
  [ECGWaveformEnum.VENTRICULAR_TACHYCARDIA]: '心室頻拍(VT)',
  [ECGWaveformEnum.PULSELESS_VT]: '無脈性VT',
  [ECGWaveformEnum.ATRIAL_FIBRILLATION]: '心房細動(Af)',
  [ECGWaveformEnum.PSVT]: '上室性頻拍(PSVT)',
  [ECGWaveformEnum.AV_BLOCK]: '房室ブロック',
  [ECGWaveformEnum.BRADYCARDIA]: '徐脈性不整脈',
  [ECGWaveformEnum.TACHYCARDIA]: '頻脈性不整脈',
  [ECGWaveformEnum.OTHER]: 'その他',
  // 以下は新潟では利用しない
  [ECGWaveformEnum.NONE]: 'なし',
  [ECGWaveformEnum.ARRHYTHMIA]: '心律不整',
  [ECGWaveformEnum.ATRIAL_FLUTTER]: '心房粗動',
  [ECGWaveformEnum.FIRST_DEGREE_AV_BLOCK]: 'Ⅰ度房室ブロック',
  [ECGWaveformEnum.SECOND_DEGREE_AV_BLOCK]: 'Ⅱ度房室ブロック',
  [ECGWaveformEnum.THIRD_DEGREE_AV_BLOCK]: 'Ⅲ度房室ブロック',
  [ECGWaveformEnum.BUNDLE_BRANCH_BLOCK]: 'ブンドルブランチブロック',
  [ECGWaveformEnum.ST_ELEVATION]: 'ST上昇',
  [ECGWaveformEnum.ST_DEPRESSION]: 'ST低下',
  [ECGWaveformEnum.PVC]: '心室性期外収縮',
  [ECGWaveformEnum.VPC]: '心室性期外収縮',
} as const satisfies Record<keyof typeof ECGWaveformEnum, string>;

/**
 * 心電図波形の日本語ラベルを取得する関数
 *
 * @param ecgWaveform - 心電図波形
 * @returns 日本語ラベル
 */
export const getECGWaveformLabel = (
  ecgWaveform: keyof typeof ECGWaveformEnum | null | undefined
): string => {
  if (!ecgWaveform || !(ecgWaveform in ECG_WAVEFORM_LABELS)) {
    return '';
  }
  return ECG_WAVEFORM_LABELS[ecgWaveform];
};

/**
 * 心電図補足の日本語ラベルマッピング
 */
const ECG_WAVE_REMARKS_LABELS = {
  [ECGWaveRemarksEnum.ST_ELEVATION]: 'ST上昇',
  [ECGWaveRemarksEnum.ST_DEPRESSION]: 'ST低下',
  [ECGWaveRemarksEnum.PVC]: '心室性期外収縮あり',
  [ECGWaveRemarksEnum.PAC]: '上室性期外収縮あり',
  // 以下は新潟では利用しない
  [ECGWaveRemarksEnum.OTHER]: 'その他',
} as const satisfies Record<keyof typeof ECGWaveRemarksEnum, string>;

/**
 * 心電図補足の日本語ラベルを取得する関数
 *
 * @param ecgWaveRemarks - 心電図補足
 * @returns 日本語ラベル
 */
export const getECGWaveRemarksLabel = (
  ecgWaveRemarks: keyof typeof ECGWaveRemarksEnum | null | undefined
): string => {
  if (!ecgWaveRemarks || !(ecgWaveRemarks in ECG_WAVE_REMARKS_LABELS)) {
    return '';
  }
  return ECG_WAVE_REMARKS_LABELS[ecgWaveRemarks];
};

/**
 * 瞳孔対光反射の日本語ラベルマッピング
 */
const PUPIL_REACTION_LABELS = {
  [PupilReactionEnum.POSITIVE]: '正常',
  [PupilReactionEnum.NEGATIVE]: '消失',
  [PupilReactionEnum.UNKNOWN]: '不明',
  // 以下は新潟では利用しない
  [PupilReactionEnum.SLUGGISH]: '鈍',
} as const satisfies Record<keyof typeof PupilReactionEnum, string>;

/**
 * 瞳孔対光反射の日本語ラベルを取得する関数
 *
 * @param pupilReaction - 瞳孔対光反射
 * @returns 日本語ラベル
 */
export const getPupilReactionLabel = (
  pupilReaction: keyof typeof PupilReactionEnum | null | undefined
): string => {
  if (!pupilReaction || !(pupilReaction in PUPIL_REACTION_LABELS)) {
    return '';
  }
  return PUPIL_REACTION_LABELS[pupilReaction];
};

/**
 * 気管挿管装置の日本語ラベルマッピング
 */
const INTUBATION_DEVICE_LABELS = {
  [IntubationDevice.LT]: 'ラリンゲルチューブ',
  [IntubationDevice.TRACHEAL_INTUBATION]: '気管挿管',
  [IntubationDevice.VIDEO_LARYNGOSCOPE]: 'ビデオ喉頭鏡',
  // 以下は新潟では利用しない
  [IntubationDevice.LM]: 'ラリンゲルマスク',
  [IntubationDevice.MANUAL]: '用手法',
  [IntubationDevice.NASAL_AIRWAY]: '経鼻エアウェイ',
  [IntubationDevice.FOREIGN_BODY_REMOVAL]: '異物除去',
  [IntubationDevice.OTHER]: 'その他',
} as const satisfies Record<keyof typeof IntubationDevice, string>;

/**
 * 気管挿管装置の日本語ラベルを取得する関数
 *
 * @param intubationDevice - 気管挿管装置
 * @returns 日本語ラベル
 */
export const getIntubationDeviceLabel = (
  intubationDevice: keyof typeof IntubationDevice | null | undefined
): string => {
  if (!intubationDevice || !(intubationDevice in INTUBATION_DEVICE_LABELS)) {
    return '';
  }
  return INTUBATION_DEVICE_LABELS[intubationDevice];
};

/**
 * 静脈路適応の日本語ラベルを取得する関数
 *
 * @param ivAccessTiming - 静脈路適応
 * @returns 日本語ラベル
 */
const TREATMENT_IV_ACCESS_TIMING_LABELS = {
  [TreatmentIvAccessTimingEnum.PRE_CPA]: '心肺停止前',
  [TreatmentIvAccessTimingEnum.POST_CPA]: '心肺停止後',
  [TreatmentIvAccessTimingEnum.GLUCOSE]: 'グルコース',
  // 以下は新潟では利用しない
  [TreatmentIvAccessTimingEnum.CPA]: 'CPA',
  [TreatmentIvAccessTimingEnum.SHOCK]: 'ショック',
  [TreatmentIvAccessTimingEnum.CRASH]: 'クラッシュ',
} as const satisfies Record<keyof typeof TreatmentIvAccessTimingEnum, string>;

/**
 * 静脈路適応の日本語ラベルを取得する関数
 *
 * @param ivAccessTiming - 静脈路適応
 * @returns 日本語ラベル
 */
export const getTreatmentIvAccessTimingLabel = (
  ivAccessTiming: keyof typeof TreatmentIvAccessTimingEnum | null | undefined
): string => {
  if (
    !ivAccessTiming ||
    !(ivAccessTiming in TREATMENT_IV_ACCESS_TIMING_LABELS)
  ) {
    return '';
  }
  return TREATMENT_IV_ACCESS_TIMING_LABELS[ivAccessTiming];
};

/**
 * 投与薬物の日本語ラベルマッピング
 */
const ADMINISTERED_MEDICATION_LABELS = {
  [AdministeredMedication.ADRENALINE]: 'アドレナリン',
  [AdministeredMedication.GLUCOSE]: 'ブドウ糖',
  [AdministeredMedication.EPINEPHRINE]: 'エピペン',
} as const satisfies Record<keyof typeof AdministeredMedication, string>;

/**
 * 投与薬物の日本語ラベルを取得する関数
 *
 * @param administeredMedication - 投与薬物
 * @returns 日本語ラベル
 */
export const getAdministeredMedicationLabel = (
  administeredMedication: keyof typeof AdministeredMedication | null | undefined
): string => {
  if (
    !administeredMedication ||
    !(administeredMedication in ADMINISTERED_MEDICATION_LABELS)
  ) {
    return '';
  }
  // @ts-ignore - TODO: API再生成後に削除
  return ADMINISTERED_MEDICATION_LABELS[administeredMedication];
};

/**
 * 交通事故状況の日本語ラベルマッピング
 */
const TRAFFIC_ACCIDENT_SITUATION_LABELS = {
  [TrafficAccidentSituationEnum.VEHICLE]: '自動車',
  [TrafficAccidentSituationEnum.MOTORCYCLE]: 'バイク',
  [TrafficAccidentSituationEnum.BICYCLE]: '自転車',
  [TrafficAccidentSituationEnum.PEDESTRIAN]: '歩行者',
} as const satisfies Record<keyof typeof TrafficAccidentSituationEnum, string>;

/**
 * 交通事故状況の日本語ラベルを取得する関数
 *
 * @param situation - 交通事故状況
 * @returns 日本語ラベル
 */
export const getTrafficAccidentSituationLabel = (
  situation: keyof typeof TrafficAccidentSituationEnum | null | undefined
): string => {
  if (!situation || !(situation in TRAFFIC_ACCIDENT_SITUATION_LABELS)) {
    return '';
  }
  return TRAFFIC_ACCIDENT_SITUATION_LABELS[situation];
};

/**
 * 交通事故事故タイプの日本語ラベルマッピング
 */
const TRAFFIC_ACCIDENT_ACCIDENT_TYPE_LABELS = {
  [TrafficAccidentAccidentTypeEnum.HEAD_ON_COLLISION]: '正面衝突',
  [TrafficAccidentAccidentTypeEnum.SIDE_COLLISION]: '側面衝突',
  [TrafficAccidentAccidentTypeEnum.REAR_END_COLLISION]: '追突',
  [TrafficAccidentAccidentTypeEnum.SINGLE_VEHICLE]: '自損',
} as const satisfies Record<
  keyof typeof TrafficAccidentAccidentTypeEnum,
  string
>;

/**
 * 交通事故事故タイプの日本語ラベルを取得する関数
 *
 * @param accidentType - 交通事故事故タイプ
 * @returns 日本語ラベル
 */
export const getTrafficAccidentAccidentTypeLabel = (
  accidentType: keyof typeof TrafficAccidentAccidentTypeEnum | null | undefined
): string => {
  if (
    !accidentType ||
    !(accidentType in TRAFFIC_ACCIDENT_ACCIDENT_TYPE_LABELS)
  ) {
    return '';
  }
  return TRAFFIC_ACCIDENT_ACCIDENT_TYPE_LABELS[accidentType];
};

/**
 * ドクターカー/ドクターヘリ出動の日本語ラベルマッピング
 */
const DR_CAR_DR_HELI_DISPATCH_LABELS = {
  [DrCarDrHeliDispatchEnum.DISPATCHED]: 'あり',
  [DrCarDrHeliDispatchEnum.NONE]: 'なし',
  [DrCarDrHeliDispatchEnum.CANCELED_IN_ROUTE]: '途中引揚げ',
} as const satisfies Record<keyof typeof DrCarDrHeliDispatchEnum, string>;

/**
 * ドクターカー/ドクターヘリ出動の日本語ラベルを取得する関数
 *
 * @param dispatch - ドクターカー/ドクターヘリ出動
 * @returns 日本語ラベル
 */
export const getDrCarDrHeliDispatchLabel = (
  dispatch: keyof typeof DrCarDrHeliDispatchEnum | null | undefined
): string => {
  if (!dispatch || !(dispatch in DR_CAR_DR_HELI_DISPATCH_LABELS)) {
    return '';
  }
  return DR_CAR_DR_HELI_DISPATCH_LABELS[dispatch];
};

export const enumTranslator = (
  data: ReturnType<typeof useIncidentDetail>['incidentDetailData']
) => {
  const { incidentInfo, emsTeamInfo, patientInfo } = data;
  return {
    incidentInfo: {
      ...incidentInfo,
      incidentType: getIncidentTypeLabel(incidentInfo.incidentType),
    },
    patientInfo: patientInfo.map(patient => {
      return {
        ...patient,
        baseInfo: {
          ...patient.baseInfo,
          sex: getPatientSexLabel(patient?.baseInfo?.sex),
          occupation: getOccupationLabel(patient?.baseInfo?.occupation),
        },
        requestSummary: {
          ...patient.requestSummary,
          medicalHistory:
            patient?.requestSummary?.medicalHistory?.map(history =>
              getRequestSummaryMedicalHistoryLabel(history)
            ) ?? [],
          adl: getRequestSummaryADLLabel(patient?.requestSummary?.adl),
          isLastMeal: getRequestSummaryIsLastMealLabel(
            patient?.requestSummary?.isLastMeal
          ),
        },
        operationCriteria: {
          ...patient.operationCriteria,
          operationCriteriaDetail: getOperationCriteriaDetailLabel(
            patient?.operationCriteria?.operationCriteriaDetail
          ),
        },
        vitals: patient.vitals?.map(vital => {
          return {
            ...vital,
            data: {
              ...vital.data,
              jcs: getJCSLabel(vital.data?.jcs),
              o2Means: getVitalO2MeanLabel(vital.data?.o2Means),
              btPart: getVitalBtPartLabel(vital.data?.btPart),
              ecgWaveform: getECGWaveformLabel(vital.data?.ecgWaveform),
              ecgRemarks: getECGWaveRemarksLabel(vital.data?.ecgRemarks),
              pupilReactionLeft: getPupilReactionLabel(
                vital.data?.pupilReactionLeft
              ),
              pupilReactionRight: getPupilReactionLabel(
                vital.data?.pupilReactionRight
              ),
              measureTiming: getMeasureTimingLabel(vital.data?.measureTiming),
            },
          };
        }),
        treatments: patient.treatments?.map(treatment => {
          return {
            ...treatment,
            data: {
              ...treatment.data,
              ivAccessTiming: getTreatmentIvAccessTimingLabel(
                treatment.data?.ivAccessTiming
              ),
              ivAccessData: treatment.data?.ivAccessData?.map(ivAccessData => ({
                ...ivAccessData,
              })),
              intubationDevice: getIntubationDeviceLabel(
                treatment.data?.intubationDevice
              ),
              administeredMedication: getAdministeredMedicationLabel(
                treatment.data?.administeredMedication
              ),
            },
          };
        }),
        trafficAccident: {
          ...patient.trafficAccident,
          accidentType: patient.trafficAccident?.accidentType.map(type =>
            getTrafficAccidentAccidentTypeLabel(type)
          ),
          patientSide: {
            ...patient.trafficAccident?.patientSide,
            accidentSituation: getTrafficAccidentSituationLabel(
              patient.trafficAccident?.patientSide?.accidentSituation
            ),
          },
          anotherSide: {
            ...patient.trafficAccident?.anotherSide,
            accidentSituation: getTrafficAccidentSituationLabel(
              patient.trafficAccident?.anotherSide?.accidentSituation
            ),
          },
        },
        drCarDrHeli: {
          ...patient.drCarDrHeli,
          drCarDispatch: getDrCarDrHeliDispatchLabel(
            patient.drCarDrHeli?.drCarDispatch
          ),
          drHeliDispatch: getDrCarDrHeliDispatchLabel(
            patient.drCarDrHeli?.drHeliDispatch
          ),
        },
      };
    }),
    emsTeamInfo: {
      ...emsTeamInfo,
      emsTeamMembers: emsTeamInfo.emsTeamMembers.map(member => ({
        ...member,
        position: getEmsTeamMemberPositionLabel(member.position),
      })),
    },
  };
};
